import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { routeMeta } from '../seo/routeMeta';

/**
 * SeoHelmet component injects SEO, Open Graph, Twitter Card, and JSON‑LD metadata.
 * It selects the appropriate meta data based on the current pathname using the
 * `routeMeta` lookup table. If a route is not explicitly defined, it falls back to
 * the home page configuration.
 */
const SeoHelmet = ({ config = {} }) => {
  const { pathname } = useLocation();

  // Match exact path or simple dynamic pattern (e.g., /job/:id)
  const matchMeta = (path) => {
    if (routeMeta[path]) return routeMeta[path];
    for (const pattern of Object.keys(routeMeta)) {
      if (pattern.includes(':')) {
        const regex = new RegExp('^' + pattern.replace(/:\\w+/g, '[^/]+') + '$');
        if (regex.test(path)) return routeMeta[pattern];
      }
    }
    return routeMeta['/'];
  };

  const routeData = matchMeta(pathname);

  const {
    title = 'Velaivendum - Tamil Nadu Job Portal',
    description = 'Find jobs, services and government schemes in Tamil Nadu.',
    keywords = 'Tamil Nadu jobs, government jobs, services, job portal',
    url = 'https://www.velaivendum.com',
    image = 'https://www.velaivendum.com/default-og-image.jpg',
    type = 'website',
    siteName = 'Velaivendum',
    author = 'Velaivendum Team',
    locale = 'en_IN',
    jsonLd = {},
  } = { ...routeData, ...config };

  // Build the canonical URL for the current page
  const canonical = new URL(pathname, url).toString();

  // Default JSON‑LD (WebSite / WebPage) – can be overridden via routeMeta.jsonLd
  const defaultJsonLd = {
    '@context': 'https://schema.org',
    '@type': type === 'article' ? 'WebPage' : 'WebSite',
    url: canonical,
    name: title,
    description,
    image,
    publisher: {
      '@type': 'Organization',
      name: siteName,
      logo: { '@type': 'ImageObject', url: image },
    },
  };
  const finalJsonLd = Object.keys(jsonLd).length ? jsonLd : defaultJsonLd;

  return (
    <Helmet>
      {/* Primary meta */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow" />
      <meta name="author" content={author} />
      <meta name="language" content="en" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Canonical link */}
      <link rel="canonical" href={canonical} />

      {/* JSON‑LD Structured Data */}
      <script type="application/ld+json">{JSON.stringify(finalJsonLd)}</script>
    </Helmet>
  );
};

export default SeoHelmet;
